import { useEffect, useState, Dispatch, SetStateAction } from "react";

import {
  useSelectTheme,
  useSelectProfile,
  useCreateDispatchActions,
  SERVICE_UNAVAILABLE_STATUS_LIST,
} from "../../store";

import {
  ContinuousInputFields,
  Button,
  AccountSetupProcessString,
  ProfileSettingsProcessString,
  ProfileSettingsProcessMethodString,
  RateLimitNotice,
} from "..";

import { RequestResult, RIF, useLoginRedirect, useRateLimitWait } from "../../lib";
export interface VerifyCodePageProps {
  email?: string;
  phone?: string;
  verifyType: "email" | "phone";
  verifySource?: string;
  accountSetupProcess?: AccountSetupProcessString;
  setAccountSetupProcess?: Dispatch<SetStateAction<AccountSetupProcessString>>;
  profileSettingsProcess?: string;
  setProfileSettingsProcess?: Dispatch<SetStateAction<ProfileSettingsProcessString>>;
  setVerifyCode?: Dispatch<SetStateAction<string>>;
  incorrectCode?: boolean;
  profileSettingsProcessMethod?: ProfileSettingsProcessMethodString;
}

export const VerifyCodePage = (props: VerifyCodePageProps) => {
  const { color, fontWeight } = useSelectTheme();

  const {
    email,
    phone,
    verifyType,
    accountSetupProcess,
    setAccountSetupProcess,
    profileSettingsProcess,
    setProfileSettingsProcess,
    setVerifyCode,
    incorrectCode,
    profileSettingsProcessMethod,
    verifySource,
  } = props;

  const displayName =
    verifyType === "email" ? email : `${phone?.slice(0, 2)}******${phone?.slice(-2)}`;

  const LENGTH_OF_VERIFY_CODE = 10;
  const CODE_ROW_WIDTH = `calc(${LENGTH_OF_VERIFY_CODE} * 50px)`;
  const [values, setValues] = useState<string[]>(Array(10).fill(""));
  // const [value, setValue] = useState('')
  const { completeLogin } = useLoginRedirect();

  const {
    doEMAIL_VERIFY_SUBMIT,
    doREQUEST_EMAIL_UPDATE,
    doPASSWORD_RESET_REQUEST,
    doEMAIL_VERIFY_REQUEST,
    doREQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT,
    doREQUEST_IDENTITY_MFA_EMAIL_CODE_RESEND,
  } = useCreateDispatchActions();

  const [requestResultOfMFALogin, setRequestResultOfMFALogin] = useState<RequestResult | null>(
    null,
  );
  const [requestResult, setRequestResult] = useState<RequestResult | null>(null);
  const [requestResultOfSendCode, setRequestResultOfSendCode] = useState<RequestResult | null>(
    null,
  );

  const profile = useSelectProfile();

  const [hasError, setHasError] = useState(incorrectCode);
  const [completed, setCompleted] = useState(false);
  const [sendCodeWording, setSendCodeWording] = useState("Send code again");

  // two waits, because the backend budgets submitting and re-sending apart: a throttled resend must
  // not stop someone sending the code they already have
  const submitWait = useRateLimitWait();
  const resendWait = useRateLimitWait();
  const resendWaiting = resendWait.waitSeconds !== null;

  useEffect(() => {
    if (hasError) return;
    setCompleted(values.join("").length === 10);
  }, [values, hasError]);

  const handleChange = (values: string[]) => {
    setValues(values);
  };

  const onSubmit = () => {
    if (setVerifyCode) setVerifyCode(values.join(""));
    if (profileSettingsProcessMethod === "change_email")
      return setProfileSettingsProcess?.("new_email_input");
    if (profileSettingsProcessMethod === "reset_password")
      return setProfileSettingsProcess?.("new_password_input");
    if (accountSetupProcess === "verify_code_for_forget_PWD")
      return setAccountSetupProcess?.("new_password_input");
    if (verifySource === "mfaLogin") {
      doREQUEST_IDENTITY_MFA_VERIFICATION_CODE_SUBMIT({
        setRequestResult: setRequestResultOfMFALogin,
        payload: {
          email,
          verificationCode: values.join(""),
        },
      });
    } else {
      doEMAIL_VERIFY_SUBMIT({
        setRequestResult,
        payload: {
          email,
          verificationCode: values.join(""),
        },
      });
    }
  };

  // only a reply that judged the code calls it incorrect: a 429 asks for a wait, and an outage is
  // announced by the connection banner
  const handleSubmitFailure = (failure: RequestResult) => {
    if (submitWait.waitIfRateLimited(failure)) return;
    if (SERVICE_UNAVAILABLE_STATUS_LIST.includes(failure.result?.statusCode)) return;
    setHasError(true);
  };

  useEffect(() => {
    if (!requestResult) return;
    if (!requestResult.success) return handleSubmitFailure(requestResult);
    setAccountSetupProcess?.("profile_initial_settings_page");
  }, [requestResult]);

  useEffect(() => {
    if (!requestResultOfMFALogin) return;
    if (!requestResultOfMFALogin.success) return handleSubmitFailure(requestResultOfMFALogin);
    completeLogin();
  }, [requestResultOfMFALogin]);

  const handleSendCodeAgain = () => {
    if (resendWaiting) return;
    if (profileSettingsProcessMethod === "change_email") {
      doREQUEST_EMAIL_UPDATE({
        setRequestResult: setRequestResultOfSendCode,
        payload: {},
      });
    } else if (
      profileSettingsProcessMethod === "reset_password" ||
      accountSetupProcess === "verify_code_for_forget_PWD"
    ) {
      doPASSWORD_RESET_REQUEST({
        setRequestResult: setRequestResultOfSendCode,
        payload: {
          email: accountSetupProcess === "verify_code_for_forget_PWD" ? email : profile?.email,
        },
      });
    } else if (verifySource === "mfaLogin") {
      // the code the password step issued, sent again; the sign-up route below mails nothing to a
      // verified account, and the MFA step would not take its code anyway
      doREQUEST_IDENTITY_MFA_EMAIL_CODE_RESEND({
        setRequestResult: setRequestResultOfSendCode,
        payload: { email: email ?? "" },
      });
    } else {
      doEMAIL_VERIFY_REQUEST({
        setRequestResult: setRequestResultOfSendCode,
        payload: {
          email,
        },
      });
    }
    setHasError(false);
  };

  useEffect(() => {
    if (!requestResultOfSendCode) return;
    if (!requestResultOfSendCode.success) {
      // this run's cleanup cancelled the timer a previous success armed to reset the label
      setSendCodeWording("Send code again");
      resendWait.waitIfRateLimited(requestResultOfSendCode);
      return;
    }
    // cleared once a code is on its way, not on the click, so a refused resend keeps what was typed
    setValues(Array(10).fill(""));
    setSendCodeWording("Code sent");
    const delay = setTimeout(() => {
      setSendCodeWording("Send code again");
    }, 5000);
    return () => clearTimeout(delay);
  }, [requestResultOfSendCode]);

  return (
    // wrapper
    <div
      data-testid="verify_code_page"
      css={{
        width: "fit-content",
        height: "300px",
        margin: "20vh auto",
        padding: "50px",
      }}
    >
      <div css={{ fontSize: "20px" }}>
        Verify Your {verifyType === "email" ? "Email Address" : "Phone Number"}
      </div>

      <div css={{ margin: "15px 0 20px 0" }}>
        {RIF(
          verifyType !== "email",
          <div>Your account has enabled Multi-Factor Authentication.</div>,
        )}
        <div>
          We've sent a ten-digit verification code to
          <span css={{ fontWeight: fontWeight.bold }}> {displayName}</span>
          {verifyType === "email" ? "" : " via text."}.
        </div>
        <div>
          Please enter it below to verify your{" "}
          {verifyType === "email" ? "email address" : "this action"}.
        </div>
      </div>

      <div>
        <div
          css={{
            position: "relative",
            display: "flex",
            width: CODE_ROW_WIDTH,
          }}
        >
          <ContinuousInputFields
            {...{
              numFields: LENGTH_OF_VERIFY_CODE,
              hasError,
              handleChange,
              values,
            }}
          />

          {RIF(
            hasError,
            <div
              css={{
                fontWeight: fontWeight.bold,
                color: color.warning,
                position: "absolute",
                right: "-100px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              Incorrect Code
            </div>,
          )}
        </div>

        <div css={{ marginTop: "25px", display: "flex", alignItems: "center" }}>
          <Button onClick={onSubmit} disabled={!completed || submitWait.waitSeconds !== null}>
            Next
          </Button>
          <div
            onClick={handleSendCodeAgain}
            aria-disabled={resendWaiting}
            css={{
              fontWeight: fontWeight.bold,
              color: resendWaiting ? color.grey_400 : color.primary,
              marginLeft: "20px",
              cursor: resendWaiting ? "default" : "pointer",
            }}
          >
            {sendCodeWording}
          </div>
        </div>

        {RIF(submitWait.waitSeconds ?? resendWait.waitSeconds, (seconds) => (
          <RateLimitNotice
            waitSeconds={seconds}
            css={{ marginTop: "20px", maxWidth: CODE_ROW_WIDTH }}
          />
        ))}
      </div>
    </div>
  );
};

// interface CellsInInputFieldProps {
//   inputValue: string
//   inputLength: number
//   hasError?: boolean
// }

// const CellsInInputField = (props: CellsInInputFieldProps) => {
//   const {color, fontWeight} = useSelectTheme()

//   const LENGTH_OF_VERIFY_CODE = 10

//   const calculateBorderStyle = (index: number) => {
//     let borderWidth = '1px'
//     let borderColor = color.grey_160

//     if (props.inputLength === index) {
//       borderWidth = '2px'
//       borderColor = color.primary
//     }

//     if (props.hasError) {
//       borderColor = color.attention
//     }

//     return `${borderWidth} solid ${borderColor}`
//   }

//   const calcuclateBoxShadowStyle = (index: number) => {
//     let boxShadow = 'none'

//     if (index === props.inputLength) {
//       boxShadow = `0px 2px 2px ${color.primary}`
//     }

//     if (props.hasError) {
//       boxShadow = `0px 2px 2px ${color.attention}`
//     }

//     return boxShadow
//   }

//   return (
//     <>
//       {[...Array(LENGTH_OF_VERIFY_CODE).keys()].map((index) => (
//         <div
//           key={index}
//           css={{
//             width: '40px',
//             height: '40px',
//             border: calculateBorderStyle(index),
//             boxShadow: calcuclateBoxShadowStyle(index),
//             borderRadius: '5px',
//             fontSize: '20px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             marginRight: '10px',
//           }}
//         >
//           {props.inputValue[index]}
//         </div>
//       ))}
//     </>
//   )
// }
